import { WebAuth } from 'auth0-js'
import Keycloak from 'keycloak-js'
import axios from 'axios'
import { computed, ref, shallowRef } from 'vue'
import { defineStore } from 'pinia'
import localforage from 'localforage'
import { jwtDecode } from 'jwt-decode'
import { setUser as setSentryUser } from '@sentry/browser'

import type { KeycloakInitOptions } from 'keycloak-js'
import type { Auth0Result, Auth0Error } from 'auth0-js'
import type { Milliseconds } from '@/utils/dataMaps'

import { config, constants } from '@/globals'

const auth0Store = () => {
  const auth0 = new WebAuth({
    clientID: config.AUTH0_CLIENT_ID,
    domain: config.AUTH0_DOMAIN,
    audience: config.AUTH0_AUDIENCE,
    responseType: 'token',
    scope: 'openid',
    redirectUri: `${window.location.origin}/callback`,
  })

  const token = shallowRef<string | null>(null)
  const tokenExpirationTimestamp = shallowRef<Milliseconds>()
  const isAuthenticated = computed(() => !!token.value)
  const debug = ref({
    tokenUpdate: true, // You can disable this in the dev tools to test the behavior of getting no new valid token
    lastUpdate: '',
  })

  // FIXME keep for cypress for now, later use https://docs.cypress.io/guides/end-to-end-testing/auth0-authentication
  if (window.Cypress) {
    const instance = axios.create({
      baseURL: config.BACKEND_URL,
    })

    auth0.logout = () => instance.get(`${config.BACKEND_URL}/auth0/logout`)
    auth0.authorize = () => instance.get(`${config.BACKEND_URL}/auth0/authorize`)
    auth0.parseHash = () => instance.get(`${config.BACKEND_URL}/auth0/callback`)

    auth0.checkSession = async (options, cb) => {
      try {
        await instance.get(`${config.BACKEND_URL}/auth0/check-auth0-session`)

        cb(null, { accessToken: 'test-token' })
      } catch {
        cb({ error: 'fake auth error' }, null)
      }
    }
  }

  function checkOAuthSession(): Promise<void> {
    return new Promise((resolve, reject) => {
      auth0.checkSession({}, async (error: null | Auth0Error, result: Auth0Result) => {
        if (error !== null) {
          await redirectToLogin()
          reject(new Error('auth0 error', { cause: error }))
          return
        }

        const newToken = result.accessToken as string

        if (debug.value.tokenUpdate) {
          // FIXME keep for cypress for now, later use https://docs.cypress.io/guides/end-to-end-testing/auth0-authentication
          const jwt = window.Cypress
            ? { exp: new Date().getTime() + 100_000 }
            : jwtDecode(newToken)

          token.value = newToken

          if (jwt.exp) {
            tokenExpirationTimestamp.value = jwt.exp * 1000
          }
        }

        debug.value.lastUpdate = (new Date()).toISOString()

        resolve()
      })
    })
  }

  function logout() {
    setSentryUser(null)

    token.value = null

    auth0.logout({
      returnTo: window.location.origin,
      clientID: config.AUTH0_CLIENT_ID,
      federated: true,
    })
  }

  /* Doesn't seem to get used anywhere - christian */
  async function redirectToLogin() {
    const url = new URL(window.location.href)
    await localforage.setItem(constants.LOCAL_STORAGE_AUTHENTICATION_REDIRECT, `${url.pathname}${url.hash}`)
    auth0.authorize()
  }

  function callback(): void {
    auth0.parseHash({ hash: window.location.hash }, (_, hash) => {
      if (hash) {
        token.value = hash.accessToken as string
      }
    })
  }

  async function clearRedirect() {
    /* Not used. */
  }

  return {
    token,
    tokenExpirationTimestamp,
    debug,
    isAuthenticated,
    redirectToLogin,
    callback,
    logout,
    checkOAuthSession,
    clearRedirect,
  }
}

const keycloakStore = () => {
  const token = shallowRef<string | null>(null)
  const tokenExpirationTimestamp = shallowRef<Milliseconds>()
  const isAuthenticated = computed(() => !!token.value)

  const KEYCLOAK_HOSTS: string[] = config.KEYCLOAK_HOSTS?.split(',') ?? [
    'login.modbuscloudconnect.net',
    'login2.modbuscloudconnect.net',
  ]

  let keycloak: Keycloak | undefined
  let lastWorkingKeycloakHost: string

  (async function tryInitializeKeycloak() {
    // FIXME keep for cypress for now
    if (window.Cypress) {
      token.value = 'test-token'
      tokenExpirationTimestamp.value = Date.now() + 3600 * 1000

      keycloak = {
        init: () => Promise.resolve(true),
        token: 'test-token',
        tokenParsed: { exp: Math.floor(Date.now() / 1000) + 3600 },
        updateToken: () => Promise.resolve(false),
        logout: () => Promise.resolve(),
      } as unknown as Keycloak

      return
    }

    for (const host of KEYCLOAK_HOSTS) {
      try {
        const baseUrl = (host.indexOf('http://') === 0 || host.indexOf('https://') === 0)
          ? host
          : `https://${host}`

        keycloak = new Keycloak({
          url: baseUrl,
          realm: config.KEYCLOAK_REALM,
          clientId: config.KEYCLOAK_CLIENT_ID,
        })

        const initOptions: KeycloakInitOptions = {
          onLoad: 'login-required',
          redirectUri: `${window.location.origin}/callback`,
          checkLoginIframe: false,
        }

        let authenticated = false

        try {
          const isAlive = await fetch(`${baseUrl}/realms/${config.KEYCLOAK_REALM}`, { method: 'GET' })
          if (!isAlive || isAlive.status !== 200) {
            console.warn(`Keycloak host ${host} is not responding as expected.`)
            continue // Try the next host
          }
          let justStoredPreRedirectUrl = false
          if (await localforage.getItem(constants.LOCAL_STORAGE_AUTHENTICATION_REDIRECT) === null &&
            window.location.pathname !== '/callback' && !window.location.pathname.includes('/service/')) {
            /* Persist existing URL requested WITH parameters. We'll loose these otherwize during
              the round trip to Keycloak. They will be picked up and removed by authenticationGuard.ts
              which will then do the final router movement to the relevant route/parameters. */
            const url = new URL(window.location.href)
            const payload = `${url.pathname}${url.search}${url.hash}`
            await localforage.setItem(constants.LOCAL_STORAGE_AUTHENTICATION_REDIRECT, payload)
            justStoredPreRedirectUrl = true
          }
          /* Try authenticate. If we haven't yet, this will abandon this current
             page (script) and redirect to Keycloak. If we have, this will just
             check if the session is still valid and refresh tokens if needed. */
          authenticated = await keycloak.init(initOptions)

          if (authenticated && justStoredPreRedirectUrl) {
            /* We just stored the URL and authenticated without needing to
               redirect to Keycloak, so we don't need to store the URL at all for
               later. Remove it. */
            await localforage.removeItem(constants.LOCAL_STORAGE_AUTHENTICATION_REDIRECT)
          }
        } catch {
          console.warn(`!! Keycloak init() failed for host ${host}`)
          continue // Try the next host
        }

        if (authenticated) {
          lastWorkingKeycloakHost = host
          // Tokens are now available in keycloak.token, keycloak.idToken, etc.
          token.value = keycloak.token ?? null
          if (keycloak.tokenParsed?.exp) {
            tokenExpirationTimestamp.value = keycloak.tokenParsed.exp * 1000
            /* Start minutely OATH token refresh check. */
            setInterval(function () {
              if (!keycloak) {
                window.location.reload()
                return
              }
              keycloak.updateToken(60).then(function (refreshed) {
                if (!keycloak) return
                if (refreshed) {
                  token.value = keycloak.token ?? null
                }
                return refreshed
              }).catch(function () {
                console.error('FAILED to refresh the token, or the session has expired')
                /* Force a page reload. This might trigger failover to our other Keycloak
                   instance which we want if that's the reason the token refresh failed. */
                window.location.reload()
              })
            }, 30000)
          }
          break // Exit the loop if initialization is successful
        }
      } catch (error) {
        console.warn(`Keycloak initialization failed for host ${host}:`, error)
      }
    }
  })()

  if (!keycloak) {
    throw new Error('Keycloak initialization failed for all hosts')
  }

  function checkOAuthSession(): Promise<void> {
    return new Promise((resolve) => {
      /* Every 200ms check if we've got a token. If we do
         cancel the timer and unblock the promise. We need this
         initially to delay the landing page loading until the
         token is acquired. This is also called when selecting
         side bar menu items to make sure the token isn't stale
         and refresh it if it is.. */
      const timer = setInterval(function () {
        /* If keycloak object not available yet, just return. */
        if (!keycloak) return

        if (token.value !== null) {
          clearInterval(timer)

          keycloak.updateToken(5).then(function (refreshed) {
            if (refreshed) {
              token.value = keycloak.token ?? null
            }
            /* if it wasn't refreshed, the token is still valid so
               nothing to do. */
            return refreshed
          }).catch(function () {
            console.error('FAILED to refresh the token, or the session has expired')
            /* Force a page reload. This might trigger failover to our other Keycloak
               instance which we want if that's the reason the token refresh failed. */
            window.location.reload()
          })

          resolve()
        }
      }, 200)
    })
  }

  async function logout() {
    try {
      const isAlive = await fetch(`${lastWorkingKeycloakHost}/realms/${config.KEYCLOAK_REALM}`, { method: 'GET' })
      if (!isAlive || isAlive.status !== 200) { throw new Error('Keycloak host not responding') }
      if (keycloak) { keycloak.logout() }
    } catch {
      console.error('Keycloak logout failed. Trying page reload.')
      window.location.reload()
    }
  }

  async function redirectToLogin() {
    /* Doesn't seem to get used anywhere - christian */
  }

  async function callback() {

    /* Not required */
  }

  async function clearRedirect() {
    await localforage.removeItem(constants.LOCAL_STORAGE_AUTHENTICATION_REDIRECT)
  }

  return {
    token,
    tokenExpirationTimestamp,
    isAuthenticated,
    redirectToLogin,
    callback,
    logout,
    checkOAuthSession,
    clearRedirect,
  }
}

const which = config.AUTH_PROVIDER ?? 'auth0' /* Choose Auth0 or Keycloak. Defaults to Auth0. */

export const useAuthenticationStore = defineStore('AuthenticationStore', which === 'auth0' ? auth0Store : keycloakStore)
